/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import {useEffect, useState} from 'react';
import {error} from '@ion/core/src/logger/logger';
import {removeElFromArray} from '@ion/core/src/utils/ObjectUtil';
import {AgGridType} from './IonGrid';

/**
 * @useGridEventListener
 * hook for setting a listener on the grid api and calling a function when
 * that event fires
 *
 * @param {object} gridApi the gridApi provided by ag-grid
 *   cta column
 * @param {string} event the grid api event that the listener should listen for
 * @param {Function} callback the function that should be called when the passed
 *   grid event fires
 *
 * ```typescript
 *
 *   useGridEventListener(gridApi, 'selectionChanged', onSelectionChanged);
 *
 * ```
 */
export const useGridEventListener = (gridApi: AgGridType, event: string, callback: Function) => {
  const [listenersInitialized, setListenersInitialized] = useState([]);

  useEffect(() => {
    if (listenersInitialized.includes(event)) {
      return error(`Grid event listener for ${event} already set`);
    }
    if (gridApi?.addEventListener) {
      gridApi.addEventListener(event, callback);
      setListenersInitialized([...listenersInitialized, event]);
      return function() {
        gridApi?.removeEventListener(event);
        setListenersInitialized(removeElFromArray(listenersInitialized, event));
      };
    }
  }, [gridApi]);
};

export default useGridEventListener;
